import { useStepStore } from "../stores/stepStore";
import { useEffect, useRef, useState } from "react";
import { Header } from "../components/header/header";
import ArrowLeftIcon from "../assets/arrowLeftIcon.svg";
import ArrowRightIcon from "../assets/arrowRightIcon.svg";
import { useCheckboxStore } from "../stores/checkBoxStore";
import { LoadingCircle } from "../components/Common/loading";
// import { VerticalStepper } from "../components/verticalStepper";
import { useIncomeStore } from "../stores/employmentDetailsStore";
// import { useDocumentStore } from "../stores/doucumentUploadStore";
import { useCoApplicantStore } from "../stores/coApplicantsStore";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAddressLookupStore } from "../stores/addressLookupStore";
import { useDownPaymentStore } from "../stores/downPaymentDetailsStore";
import { useUserMortgageProfileStore } from "../stores/userProfileStore";
import { convertToNumberAndMultiplyBy100 } from "../utils/convertAmount";
import { useConsentApplicantsStore } from "../stores/creditConsentStore";
import { usePersonalInfoStore } from "../stores/personalInformationStore";
import { formatPhoneWithFallback } from "../utils/formatPhoneWithFallback";
import { AddressLookupModal } from "../components/Common/AddressLookup/addressLookupModal";
import {
  // getProfile,
  postMortgageDetails,
  submitMortgageDetails,
  // submitMortgageDetails,
  // submitFiles,
} from "../services/mortgageDetails";
import type {
  // MortgageStatusEnum,
  CanadianProvinceCodeEnum,
  CoApplicantIncomeSourceEnum,
  DownPaymentSourceEnum,
  MaritalStatusEnum,
  RelationshipEnum,
} from "../interfaces/mortgageDetailsInterface";
import { CircularLoading } from "../components/Common/circularLoading";

export const MortgageApplicationLayout = () => {
  const topBarRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  const { setUserMortgageProfile } = useUserMortgageProfileStore();
  const [loading, setLoading] = useState(false);

  const [stepperDimensions, setStepperDimensions] = useState({
    width: 0,
    height: 0,
  });

  const step = useStepStore((state) => state.step);
  const maxSteps = useStepStore((state) => state.maxSteps);
  const setStep = useStepStore((state) => state.setStep);
  const { isChecked } = useCheckboxStore();
  const { validatePersonalInfo } = usePersonalInfoStore();
  const { addressLookupModalOpen } = useAddressLookupStore();
  const { addedIncomes, validateIncomeFields, incomeData } = useIncomeStore();
  const { coApplicants, validateCoApplicants } = useCoApplicantStore();
  // const { validateDocumentFields } = useDocumentStore();
  const { validateConsent, validateConsentApplicants, consentApplicants } =
    useConsentApplicantsStore();

  const { sources, validateSources } = useDownPaymentStore();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const updateHeights = () => {
      if (topBarRef.current) {
        setTopBarHeight(topBarRef.current.offsetHeight);
      }
      if (stepperRef.current) {
        setStepperDimensions({
          width: stepperRef.current.offsetWidth,
          height: stepperRef.current.offsetHeight,
        });
      }
    };

    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);



  useEffect(() => {
    if (step === 2 && addedIncomes.length === 0) {
      setNextButtonDisabled(true);
    } else if (step === 4 && sources.length === 0) {
      setNextButtonDisabled(true);
    } else if (step === 6 && !isChecked) {
      setNextButtonDisabled(true);
    } else {
      setNextButtonDisabled(false);
    }
  }, [step, addedIncomes, sources, isChecked]);

  useEffect(() => {
    const pathToStepMap: { [key: string]: number } = {
      "/": 1,
      "/employment-details": 2,
      "/co-applicants": 3,
      "/down-payment": 4,
      "/credit-consent": 5,
      // "/review-info": 6,
      // "/confirmation": 7,
      // "/documents": 8,
      // "/submitted": 9,
      // "/pending-contact": 10,
    };

    const currentStep = pathToStepMap[location.pathname];
    if (currentStep && currentStep !== step) {
      setStep(currentStep);
    }
  }, [location.pathname, setStep, step]);

  const handleStepChange = (newStep: number) => {
    const stepToPathMap: Record<number, string> = {
      1: "/",
      2: "/employment-details",
      3: "/co-applicants",
      4: "/down-payment",
      5: "/credit-consent",
      // 6: "/review-info",
      // 7: "/confirmation",
      // 8: "/documents",
      // 9: "/submitted",
      // 10: "/pending-contact",
    };
    const newPath = stepToPathMap[newStep];
    if (newPath) {
      navigate(newPath);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      window.parent.postMessage(
        { type: "CLOSE_MORTGAGE_APPLICATION" },
        "*"
      );
    }
    if (step > 1) {
      const newStep = step - 1;
      setStep(newStep);
      handleStepChange(newStep);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (validatePersonalInfo()) {
        try {
          setButtonsDisabled(true);
          const {
            firstName,
            middleName,
            lastName,
            address,
            address2,
            city,
            postalCode,
            province,
            phone,
            dateOfBirth,
            maritalStatus,
            isFirstTimeHomeBuyer,
            hasReferralCode,
            referralCode,
            // totalMonthlyDebt,
          } = usePersonalInfoStore.getState();
          const formattedPhone = formatPhoneWithFallback(phone);

          const formattedAddress = {
            address1: address,
            address2: address2 || "",
            city: city,
            state: province as CanadianProvinceCodeEnum,
            postalCode: postalCode,
            country: "CA",
          };
          const response = await postMortgageDetails({
            firstName: firstName.trim(),
            middleName: middleName.trim(),
            lastName: lastName.trim(),
            dateOfBirth: dateOfBirth as Date,
            address: formattedAddress,
            phoneNumber: formattedPhone,
            maritalStatus: maritalStatus as MaritalStatusEnum,
            isFirstTimeHomeBuyer: isFirstTimeHomeBuyer as boolean,
            referralCode: hasReferralCode ? referralCode.replace(/[\s-]/g, "") : undefined,
            // totalMonthlyDebt: {
            //   amount: convertToNumberAndMultiplyBy100(totalMonthlyDebt) || 0,
            //   currency: "CAD",
            // },
          });
          setUserMortgageProfile(response);
          setButtonsDisabled(false);
        } catch (error) {
          console.error("Error while sending mortgage details:", error);
          setButtonsDisabled(false);
          throw error;
        }
      } else {
        return;
      }
    }

    if (step === 2) {
      if (validateIncomeFields() && addedIncomes.length > 0) {
        try {
          setButtonsDisabled(true);
          const incomeSources = {
            fullTime: addedIncomes
              .filter((income) => income.key === "fullTime")
              .map((income) => {
                const basePay = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualBasePay || "0"
                );
                const bonuses = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualBonuses || "0"
                );
                const overtime = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualOvertime || "0"
                );
                const commission = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualCommission || "0"
                );

                return {
                  ...(incomeData[income.id]?.employerName && {
                    employerName: incomeData[income.id]?.employerName.trim(),
                  }),
                  ...(incomeData[income.id]?.jobTitle && {
                    jobTitle: incomeData[income.id]?.jobTitle.trim(),
                  }),
                  ...(incomeData[income.id]?.startDate && {
                    startDate: incomeData[income.id]?.startDate as Date,
                  }),
                  annualBasePay: { amount: basePay, currency: "CAD" },
                  annualBonuses: { amount: bonuses, currency: "CAD" },
                  annualOvertime: { amount: overtime, currency: "CAD" },
                  annualCommission: { amount: commission, currency: "CAD" },
                };
              }),

            partTime: addedIncomes
              .filter((income) => income.key === "partTime")
              .map((income) => {
                const basePay = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualBasePay || "0"
                );
                const bonuses = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualBonuses || "0"
                );
                const overtime = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualOvertime || "0"
                );
                const commission = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualCommission || "0"
                );

                return {
                  ...(incomeData[income.id]?.employerName && {
                    employerName: incomeData[income.id]?.employerName.trim(),
                  }),
                  ...(incomeData[income.id]?.jobTitle && {
                    jobTitle: incomeData[income.id]?.jobTitle.trim(),
                  }),
                  ...(incomeData[income.id]?.startDate && {
                    startDate: incomeData[income.id]?.startDate as Date,
                  }),
                  annualBasePay: { amount: basePay, currency: "CAD" },
                  annualBonuses: { amount: bonuses, currency: "CAD" },
                  annualOvertime: { amount: overtime, currency: "CAD" },
                  annualCommission: { amount: commission, currency: "CAD" },
                };
              }),

            selfEmployment: addedIncomes
              .filter((income) => income.key === "selfEmployed")
              .map((income) => {
                const basePay = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualBasePay || "0"
                );
                const incomeAmount = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualIncome || "0"
                );

                return {
                  ...(incomeData[income.id]?.businessName && {
                    businessName: incomeData[income.id]?.businessName.trim(),
                  }),
                  ...(incomeData[income.id]?.dateEstablished && {
                    dateEstablished: incomeData[income.id]
                      ?.dateEstablished as Date,
                  }),
                  ...(incomeData[income.id]?.approximateStartDate && {
                    startDate: incomeData[income.id]
                      ?.approximateStartDate as Date,
                  }),
                  ...(incomeData[income.id]?.operatingType && {
                    operatingType: incomeData[income.id]?.operatingType,
                  }),
                  ...(incomeData[income.id]?.jobTitle && {
                    jobTitle: incomeData[income.id]?.jobTitle.trim(),
                  }),

                  annualIncome: { amount: incomeAmount | 0, currency: "CAD" },
                  annualBasePay: { amount: basePay, currency: "CAD" },
                };
              }),

            rentalIncome: addedIncomes
              .filter((income) => income.key === "rentalIncome")
              .map((income) => {
                const propertyValue = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.propertyValue || "0"
                );
                const monthlyRent = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.monthlyRent || "0"
                );
                const annualAmount = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualAmount || "0"
                );

                return {
                  ...(incomeData[income.id]?.propertyType && {
                    propertyType: incomeData[income.id]?.propertyType,
                  }),
                  propertyAddress: {
                    ...(incomeData[income.id]?.address && {
                      address1: incomeData[income.id]?.address,
                    }),
                    address2: "",
                    ...(incomeData[income.id]?.city && {
                      city: incomeData[income.id]?.city,
                    }),
                    ...(incomeData[income.id]?.province && {
                      state: incomeData[income.id]?.province,
                    }),
                    ...(incomeData[income.id]?.postalCode && {
                      postalCode: incomeData[income.id]?.postalCode,
                    }),
                    country: "CA",
                  },

                  propertyValue: {
                    amount: propertyValue | 0,
                    currency: "CAD",
                  },

                  monthlyRent: { amount: monthlyRent | 0, currency: "CAD" },

                  annualAmount: { amount: annualAmount | 0, currency: "CAD" },

                  ...(incomeData[income.id]?.rentalIncomeSince && {
                    rentalIncomeSince: incomeData[income.id]
                      ?.rentalIncomeSince as Date,
                  }),
                };
              }),

            otherIncome: addedIncomes
              .filter((income) => income.key === "otherIncome")
              .map((income) => {
                const annualAmount = convertToNumberAndMultiplyBy100(
                  incomeData[income.id]?.annualAmount || "0"
                );

                return {
                  ...(incomeData[income.id]?.incomeType && {
                    incomeType: incomeData[income.id]?.incomeType,
                  }),

                  annualAmount: { amount: annualAmount | 0, currency: "CAD" },

                  ...(incomeData[income.id]?.approximateStartDate && {
                    startDate: incomeData[income.id]
                      ?.approximateStartDate as Date,
                  }),
                };
              }),
          };
          const response = await postMortgageDetails({
            incomeSource: incomeSources,
          });
          setUserMortgageProfile(response);
          setButtonsDisabled(false);
        } catch (error) {
          console.error("Error while sending mortgage details:", error);
          setButtonsDisabled(false);
          throw error;
        }
      } else {
        return;
      }
    }

    if (step === 3) {
      if (validateCoApplicants()) {
        setButtonsDisabled(true);
        const coApplicantsData = coApplicants.map((applicant) => ({
          firstName: applicant.firstName.trim(),
          middleName: applicant.middleName.trim(),
          lastName: applicant.lastName.trim(),
          address: {
            address1: applicant.address,
            address2: applicant.address2,
            city: applicant.city,
            state: applicant.province as CanadianProvinceCodeEnum,
            postalCode: applicant.postalCode,
            country: "CA",
          },
          annualBasePay: {
            amount:
              convertToNumberAndMultiplyBy100(applicant.annualBasePay) || 0,
            currency: "CAD",
          },
          // totalMonthlyDebt: {
          //   amount:
          //     convertToNumberAndMultiplyBy100(applicant.totalMonthlyDebt) || 0,
          //   currency: "CAD",
          // },
          emailAddress: applicant.email,
          isFirstTimeHomeBuyer: applicant.isFirstTimeHomeBuyer as boolean,
          phoneNumber: formatPhoneWithFallback(applicant.phone),
          dateOfBirth: applicant.dateOfBirth as Date,
          maritalStatus: applicant.maritalStatus as MaritalStatusEnum,
          relationship: applicant.relation as RelationshipEnum,
          incomeSource:
            applicant.primaryIncomeSource as CoApplicantIncomeSourceEnum,
        }));
        const response = await postMortgageDetails({
          coApplicants: coApplicantsData,
        });
        setButtonsDisabled(false);
        setUserMortgageProfile(response);
      } else {
        return;
      }
    }

    if (step === 4) {
      if (validateSources() && sources.length > 0) {
        const downPaymentSources = sources.map((source) => ({
          type: source.source as DownPaymentSourceEnum,
          amount: {
            amount: convertToNumberAndMultiplyBy100(source.amount) || 0,
            currency: "CAD",
          },
        }));
        const response = await postMortgageDetails({
          downPaymentSources,
        });
        setUserMortgageProfile(response);
      } else {
        return;
      }
    }

    if (step === 5) {
      if (validateConsent() && validateConsentApplicants()) {
        setButtonsDisabled(true);
        setButtonsDisabled(true);
        const creditConsents = consentApplicants.map((applicant) => ({
          firstName: applicant.firstName.trim(),
          middleName: applicant.middleName.trim(),
          lastName: applicant.lastName.trim(),
          address: {
            address1: applicant.address,
            address2: applicant.address2,
            city: applicant.city,
            state: applicant.province as CanadianProvinceCodeEnum,
            postalCode: applicant.postalCode,
            country: "CA",
          },
          phoneNumber: formatPhoneWithFallback(applicant.phone),
          dateOfBirth: applicant.dateOfBirth as Date,
        }));

        const response = await postMortgageDetails({ creditConsents });
        await submitMortgageDetails();
        setButtonsDisabled(false);
        setUserMortgageProfile(response);
        window.parent.postMessage(
          { type: "CREDIT_CONSENT_SUBMITTED", creditConsents },
          "*"
        );
      } else {
        return;
      }
    }

    // if (step === 6) {
    //   try {
    //     setButtonsDisabled(true);
    //     setLoading(true);
    //     await submitMortgageDetails();
    //     const accountData = await getProfile();
    //     setUserMortgageProfile(accountData);
    //     setButtonsDisabled(false);
    //     setLoading(false);
    //     if (accountData.status === MortgageStatusEnum.PendingContact) {
    //       const newStep = 10;
    //       setStep(newStep);
    //       handleStepChange(newStep);
    //     }
    //   } catch (error) {
    //     console.error("Error while submitting mortgage details:", error);
    //     setButtonsDisabled(false);
    //   }
    // }

    // if (step === 8) {
    //   if (validateDocumentFields()) {
    //     setButtonsDisabled(true);

    //     try {
    //       await submitFiles();
    //       const accountData = await getProfile();
    //       setUserMortgageProfile(accountData);
    //       setButtonsDisabled(false);
    //     } catch (error) {
    //       console.error("Error while submitting mortgage details:", error);
    //       setButtonsDisabled(false);
    //     }
    //   } else {
    //     return;
    //   }
    // }
    if (step < maxSteps) {
      const newStep = step + 1;
      setStep(newStep);
      handleStepChange(newStep);
    }
  };

  // const getVerticalStep = () => {
  //   if (step <= 1) {
  //     return 0;
  //   }
  //   if (step <= 4) {
  //     return 1;
  //   }
  //   return 2;
  // };

  return (
    <div className="absolute inset-0 h-full w-full">
      <div className="fixed top-0 w-full z-10" ref={topBarRef}>
        <Header />
      </div>
      <div
        className="flex items-start justify-center w-full"
        style={{ paddingTop: `${topBarHeight + 30}px`, paddingBottom: "60px" }}
      >
        {loading ? (
          <div className="w-full flex flex-col items-center justify-center text-center gap-6 absolute inset-0">
            <div className="font-semibold text-4xl">Calculating...</div>
            <div>Estimated time: 5 seconds</div>
            <LoadingCircle />
          </div>
        ) : (
          <div className="max-w-screen-xl w-full flex gap-14 justify-between px-4">
            {/* {step < 7 && (
              <div
                className="w-fit hidden lg:block fixed pr-10"
                ref={stepperRef}
              >
                <VerticalStepper currentStep={getVerticalStep()} />
              </div>
            )} */}
            <div
              className="w-full space-y-6"
              style={{
                // paddingLeft: `${step < 7 ? stepperDimensions.width : 0}px`,
              }}

            >
              <Outlet />
              {step !== 7 && step !== 9 && step !== 10 && (
                <div className="flex items-center justify-between w-full gap-6">
                  <button
                    type="button"
                    className="border-2 rounded-[8px] flex items-center justify-center gap-2 border-z-dark-moss-green font-medium text-sm w-full lg:w-fit text-z-dark-moss-green px-4 py-4"
                    onClick={handleBack}
                    disabled={buttonsDisabled}
                    onTouchStart={handleBack}
                  >
                    <img src={ArrowLeftIcon} alt="" />
                    Back
                  </button>
                  <button
                    type="button"
                    className="rounded-[8px] relative flex items-center justify-center gap-2 bg-z-dark-moss-green border-2 border-z-dark-moss-green w-full lg:w-fit disabled:bg-[#DBDBDB] disabled:border-[#DBDBDB] font-medium text-sm text-white px-4 py-4"
                    onClick={handleNext}
                    disabled={buttonsDisabled || nextButtonDisabled}
                    onTouchStart={handleNext}

                  >

                    {buttonsDisabled || nextButtonDisabled ? (<CircularLoading />) : (<>
                      {step === 6 ? "Submit application" : "Next"}
                      <img src={ArrowRightIcon} alt="" /></>)}

                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {Object.keys(addressLookupModalOpen).map((type) =>
        addressLookupModalOpen[type] ? (
          <AddressLookupModal key={type} type={type} />
        ) : null
      )}
    </div>
  );
};
